/**
 * Unified Contribute/SignIn Modal
 *
 * For registered users:
 * - Add Funds (Stripe checkout)
 * - Sign Back In (magic link for existing users/gift recipients)
 *
 * For anonymous users (registration closed):
 * - Closed-registration message
 * - Sign Back In (for existing users only)
 */

import React, { useState, useCallback, useEffect } from 'react';
import { X, Mail, Loader2, CheckCircle } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { sendMagicLink } from '../services/authService';

const DEFAULT_INTRO_TEXT = "ionoff is a family hobby project, but AIs cost real money to use. A community honor system for cost sharing proved financially unsustainable. So now we're trying for sustainable marginal user costs by requiring each user to pay for what their individual usage costs, nothing more, nothing less.";

const CLOSED_REGISTRATION_TEXT = "ionoff is a family hobby project, and the friends and family registration period has ended. Please stay tuned for a bring-your-own-key (BYOK) fork coming soon.";

interface ContributeModalProps {
  isOpen: boolean;
  onClose: () => void;
  isRegisteredUser?: boolean;
}

const PRESET_AMOUNTS = [5, 10, 20, 50];
const MIN_AMOUNT = 5; // $5 minimum for Stripe

type SignInState = 'idle' | 'checking' | 'sending' | 'sent' | 'not_recognized';

export const ContributeModal: React.FC<ContributeModalProps> = ({
  isOpen,
  onClose,
  isRegisteredUser = false,
}) => {
  // Intro text from Firestore config
  const [introText, setIntroText] = useState(DEFAULT_INTRO_TEXT);

  // Add Funds state
  const [amount, setAmount] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sign In state
  const [email, setEmail] = useState('');
  const [signInState, setSignInState] = useState<SignInState>('idle');
  const [signInError, setSignInError] = useState('');

  // Fetch intro text from Firestore config (only for registered users)
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const db = getFirestore();
        const configDoc = await getDoc(doc(db, 'system', 'config'));
        if (configDoc.exists()) {
          const data = configDoc.data();
          if (data?.AddFundsIntroLanguage) {
            setIntroText(data.AddFundsIntroLanguage);
          }
        }
      } catch (err) {
        console.error('Error fetching config:', err);
        // Use default text on error
      }
    };
    if (isOpen && isRegisteredUser) {
      fetchConfig();
    }
  }, [isOpen, isRegisteredUser]);

  const selectedAmount = amount || customAmount;
  const numericAmount = parseFloat(selectedAmount) || 0;

  const handlePresetClick = (preset: number) => {
    setAmount(preset.toString());
    setCustomAmount('');
    setError(null);
  };

  const handleCustomAmountChange = (value: string) => {
    setCustomAmount(value);
    setAmount(''); // Clear preset selection
    setError(null);
  };

  const handleStripeCheckout = useCallback(async () => {
    if (numericAmount < MIN_AMOUNT) {
      setError(`Minimum amount is $${MIN_AMOUNT}`);
      return;
    }

    if (numericAmount > 1000) {
      setError('Maximum amount is $1000');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const functions = getFunctions();
      const createCheckout = httpsCallable(functions, 'createContributeCheckout');

      const result = await createCheckout({
        amount: numericAmount,
        successUrl: `${window.location.origin}/?contribution=success`,
        cancelUrl: `${window.location.origin}/?contribution=cancelled`,
      });

      const data = result.data as { url?: string; sessionId?: string };

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err: any) {
      console.error('Error creating checkout:', err);
      setError(err.message || 'Failed to start checkout. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [numericAmount]);

  const handleSendSignInLink = useCallback(async () => {
    if (!email.trim()) {
      setSignInError('Please enter your email address.');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setSignInError('Please enter a valid email address.');
      return;
    }

    setSignInState('checking');
    setSignInError('');

    try {
      // Check if email is eligible for magic link
      const functions = getFunctions();
      const checkEligibility = httpsCallable(functions, 'canSendMagicLink');
      const result = await checkEligibility({ email: email.trim() });
      const data = result.data as { canSend: boolean; reason: string };

      if (!data.canSend) {
        setSignInState('not_recognized');
        return;
      }

      // Email is eligible - send magic link
      setSignInState('sending');
      await sendMagicLink(email.trim());
      setSignInState('sent');
    } catch (err: any) {
      console.error('Error in sign-in flow:', err);
      setSignInError(err.message || 'Failed to send sign-in link. Please try again.');
      setSignInState('idle');
    }
  }, [email]);

  const resetSignIn = useCallback(() => {
    setEmail('');
    setSignInState('idle');
    setSignInError('');
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
      onKeyDown={handleKeyDown}
    >
      <div
        className="relative bg-background border border-border rounded-lg p-6 w-full max-w-md mx-4 shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* ANONYMOUS USER: Registration closed */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        {!isRegisteredUser && (
          <>
            <p className="text-sm text-muted-foreground mb-6 pr-6">
              {CLOSED_REGISTRATION_TEXT}
            </p>

            {/* Sign Back In for existing users */}
            <div className="border-t border-border pt-6">
              <h3 className="text-center text-sm font-semibold mb-4 text-muted-foreground">
                Already have an account?
              </h3>

              {/* Idle state - show email input */}
              {signInState === 'idle' && (
                <>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="relative flex-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <input
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleSendSignInLink()}
                        className="w-full pl-10 pr-4 py-2 border border-border rounded bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                  </div>

                  {signInError && (
                    <p className="text-sm text-red-500 mb-3">{signInError}</p>
                  )}

                  <button
                    type="button"
                    onClick={handleSendSignInLink}
                    className="w-full px-4 py-2 text-sm border border-border rounded hover:bg-muted transition-colors"
                    disabled={!email.trim()}
                  >
                    Sign In
                  </button>
                </>
              )}

              {/* Checking state */}
              {signInState === 'checking' && (
                <div className="text-center py-4">
                  <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">Checking email...</p>
                </div>
              )}

              {/* Sending state */}
              {signInState === 'sending' && (
                <div className="text-center py-4">
                  <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">Sending sign-in link...</p>
                </div>
              )}

              {/* Sent state */}
              {signInState === 'sent' && (
                <div className="text-center py-4">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
                  <p className="text-sm font-medium mb-1">Check your email!</p>
                  <p className="text-sm text-muted-foreground mb-3">
                    We sent a sign-in link to <span className="font-medium">{email}</span>
                  </p>
                  <button
                    type="button"
                    onClick={resetSignIn}
                    className="text-xs text-muted-foreground hover:text-foreground underline"
                  >
                    Use a different email
                  </button>
                </div>
              )}

              {/* Not recognized state */}
              {signInState === 'not_recognized' && (
                <div className="text-center py-4">
                  <p className="text-sm text-muted-foreground mb-3">
                    No account found for <span className="font-medium">{email}</span>
                  </p>
                  <button
                    type="button"
                    onClick={resetSignIn}
                    className="text-xs text-muted-foreground hover:text-foreground underline"
                  >
                    Try a different email
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* REGISTERED USER: Full Add Funds + Sign In experience */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        {isRegisteredUser && (
          <>
            {/* EUMPWU Explainer - fetched from Firestore system/config */}
            <p className="text-sm text-muted-foreground mb-6 pr-6">
              {introText}
            </p>

            {/* ADD FUNDS SECTION */}
            <h3 className="text-center text-lg font-semibold mb-4">Add Funds</h3>

            {/* Preset amounts */}
            <div className="grid grid-cols-4 gap-2 mb-3">
              {PRESET_AMOUNTS.map(preset => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => handlePresetClick(preset)}
                  className={`py-2 px-3 rounded border text-sm font-medium transition-colors ${
                    amount === preset.toString()
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background border-border hover:bg-muted'
                  }`}
                >
                  ${preset}
                </button>
              ))}
            </div>

            {/* Custom amount */}
            <div className="flex items-center gap-2 mb-4">
              <span className="text-lg text-muted-foreground">$</span>
              <input
                type="number"
                step="0.01"
                min="0.01"
                max="1000"
                placeholder="Other"
                value={customAmount}
                onChange={e => handleCustomAmountChange(e.target.value)}
                className={`flex-1 px-3 py-2 border rounded bg-background focus:outline-none focus:ring-2 focus:ring-primary ${
                  customAmount ? 'border-primary' : 'border-border'
                }`}
                disabled={isSubmitting}
              />
            </div>

            {error && (
              <p className="text-sm text-red-500 mb-3">{error}</p>
            )}

            {/* Stripe checkout button */}
            <button
              type="button"
              onClick={handleStripeCheckout}
              className="w-full px-4 py-3 text-sm bg-primary text-primary-foreground rounded hover:opacity-90 transition-opacity disabled:opacity-50 mb-6"
              disabled={isSubmitting || !selectedAmount || numericAmount < MIN_AMOUNT}
            >
              {isSubmitting ? (
                'Processing...'
              ) : (
                'Let Stripe (not ionoff) Handle My Private Payment Information'
              )}
            </button>

            {/* SIGN BACK IN SECTION */}
            <div className="border-t border-border pt-6">
              <h3 className="text-center text-lg font-semibold mb-4">Sign Back In</h3>

              {/* Idle state - show email input */}
              {signInState === 'idle' && (
                <>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="relative flex-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <input
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleSendSignInLink()}
                        className="w-full pl-10 pr-4 py-2 border border-border rounded bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                  </div>

                  {signInError && (
                    <p className="text-sm text-red-500 mb-3">{signInError}</p>
                  )}

                  <button
                    type="button"
                    onClick={handleSendSignInLink}
                    className="w-full px-4 py-2 text-sm border border-border rounded hover:bg-muted transition-colors"
                    disabled={!email.trim()}
                  >
                    Send Link
                  </button>
                </>
              )}

              {/* Checking state */}
              {signInState === 'checking' && (
                <div className="text-center py-4">
                  <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">Checking email...</p>
                </div>
              )}

              {/* Sending state */}
              {signInState === 'sending' && (
                <div className="text-center py-4">
                  <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">Sending sign-in link...</p>
                </div>
              )}

              {/* Sent state */}
              {signInState === 'sent' && (
                <div className="text-center py-4">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
                  <p className="text-sm font-medium mb-1">Check your email!</p>
                  <p className="text-sm text-muted-foreground mb-3">
                    We sent a sign-in link to <span className="font-medium">{email}</span>
                  </p>
                  <button
                    type="button"
                    onClick={resetSignIn}
                    className="text-xs text-muted-foreground hover:text-foreground underline"
                  >
                    Use a different email
                  </button>
                </div>
              )}

              {/* Not recognized state */}
              {signInState === 'not_recognized' && (
                <div className="text-center py-4">
                  <p className="text-sm text-muted-foreground mb-2">
                    Email not recognized:
                  </p>
                  <p className="text-sm font-medium mb-3">{email}</p>
                  <p className="text-xs text-muted-foreground mb-3">
                    Add funds above to create your account.
                  </p>
                  <button
                    type="button"
                    onClick={resetSignIn}
                    className="text-xs text-muted-foreground hover:text-foreground underline"
                  >
                    Try a different email
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
